/**
 * Mock-login page. The MVP has no real auth — this form simulates the
 * `POST /api/auth/mock-login` response so the dashboard shell can exercise
 * the role gates end-to-end.
 *
 * The UI offers the four roles tracked by `UiRole` (`student` is included
 * so the role-gating test can drive the 403 path). Real production auth
 * lands in Sprint 2+ and reuses the same `SessionProvider` contract.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import type { UiRole } from '../api/types';

const ROLES: ReadonlyArray<{ value: UiRole; label: string; route: string }> = [
  { value: 'teacher', label: 'Teacher', route: '/teacher/dashboard' },
  { value: 'admin', label: 'Admin', route: '/admin/dashboard' },
  { value: 'student', label: 'Student', route: '/student/dashboard' },
  { value: 'support', label: 'Support', route: '/teacher/dashboard' },
];

export function Login() {
  const { signIn, status, error } = useSession();
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState('tenant_mock');
  const [role, setRole] = useState<UiRole>('teacher');
  const [userId, setUserId] = useState('mock-user-teacher');

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const out = await signIn({ tenantId, role, userId: userId || undefined });
    if (!out.ok) return;
    const target = ROLES.find((r) => r.value === role)?.route ?? '/teacher/dashboard';
    navigate(target, { replace: true });
  }

  return (
    <main className="page page--centered" data-testid="login-page">
      <div className="card">
        <h1>Sign in (mock)</h1>
        <p className="muted">
          Local MVP only. No real Canvas connection, no real student data. Tokens
          are scoped to <code>tenant_mock</code> by default.
        </p>
        <form onSubmit={onSubmit} aria-describedby="login-help">
          <p id="login-help" className="visually-hidden">
            Mock login for the local Teacher AI Hub MVP.
          </p>

          <label className="field">
            <span>Tenant</span>
            <input
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              required
              autoComplete="off"
              data-testid="login-tenant"
            />
          </label>

          <label className="field">
            <span>User ID</span>
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              autoComplete="off"
              data-testid="login-user"
            />
          </label>

          <fieldset className="field">
            <legend>Role</legend>
            {ROLES.map((r) => (
              <label key={r.value} className="radio">
                <input
                  type="radio"
                  name="role"
                  value={r.value}
                  checked={role === r.value}
                  onChange={() => {
                    setRole(r.value);
                    setUserId(`mock-user-${r.value}`);
                  }}
                  data-testid={`login-role-${r.value}`}
                />
                <span>{r.label}</span>
              </label>
            ))}
          </fieldset>

          {error ? (
            <p
              role="alert"
              className="error"
              data-testid="login-error"
            >
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn btn--primary"
            disabled={status === 'loading'}
            data-testid="login-submit"
          >
            {status === 'loading' ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </main>
  );
}
